import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { supabaseAddComandaLine } from "./useStayComanda";
import type { StayCoverageTarget } from "./stayReservationCoverage";
import { invalidateStayReservationCoverageQueries } from "../reservation/stayReservationQueryInvalidation";

export interface SwapStayReservationCoverageInput {
  reservationId: string;
  module: StayPlanModule;
  target: StayCoverageTarget;
  dailyItemName: string;
  dailyRate: number | null;
}

interface ScheduledDayRow {
  id: string;
  stay_date: string;
  status: string;
  daily_charge_sale_item_id: string | null;
}

function validateTarget(target: StayCoverageTarget) {
  if (target.kind !== "avulso" && !target.contractId) throw new Error("coverage_contract_required");
  if (target.kind === "avulso" && target.contractId) throw new Error("coverage_contract_not_allowed");
}

async function readScheduledDays(reservationId: string): Promise<ScheduledDayRow[]> {
  const { data, error } = await (supabase as any)
    .from("stay_reservation_days")
    .select("id, stay_date, status, daily_charge_sale_item_id")
    .eq("stay_reservation_id", reservationId)
    .eq("status", "scheduled");
  if (error) throw error;
  return (data ?? []) as ScheduledDayRow[];
}

async function readOpenSaleId(reservationId: string): Promise<string> {
  const { data, error } = await (supabase as any)
    .from("sales")
    .select("id")
    .eq("stay_reservation_id", reservationId)
    .eq("status", "ABERTA")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data?.id) throw new Error("stay_comanda_not_found");
  return data.id as string;
}

export async function swapStayReservationCoverage(input: SwapStayReservationCoverageInput): Promise<void> {
  validateTarget(input.target);
  if (input.target.kind === "avulso" && input.dailyRate === null) {
    throw new Error("daily_rate_not_configured");
  }

  const saleId = input.target.kind === "avulso" ? await readOpenSaleId(input.reservationId) : null;
  const days = await readScheduledDays(input.reservationId);

  for (const day of days) {
    const { error } = await (supabase as any).rpc("release_stay_plan_units", {
      p_module: input.module,
      p_reservation_id: input.reservationId,
      p_used_on: day.stay_date,
      p_source: "hold",
    });
    if (error) throw error;
  }

  if (input.target.kind === "avulso") {
    for (const day of days) {
      const inserted = await supabaseAddComandaLine({
        saleId: saleId as string,
        itemName: input.dailyItemName,
        unitPrice: input.dailyRate as number,
      });
      const { error } = await (supabase as any)
        .from("stay_reservation_days")
        .update({ daily_charge_sale_item_id: inserted.id })
        .eq("id", day.id);
      if (error) throw error;
    }
    return;
  }

  for (const day of days) {
    const { error } = await (supabase as any).rpc("consume_stay_plan_units", {
      p_module: input.module,
      p_contract_id: input.target.contractId,
      p_reservation_id: input.reservationId,
      p_used_on: day.stay_date,
      p_units: 1,
      p_source: "hold",
    });
    if (error) throw error;
  }

  for (const day of days) {
    if (!day.daily_charge_sale_item_id) continue;
    const { error: deleteError } = await (supabase as any)
      .from("sale_items")
      .delete()
      .eq("id", day.daily_charge_sale_item_id);
    if (deleteError) throw deleteError;
    const { error: clearError } = await (supabase as any)
      .from("stay_reservation_days")
      .update({ daily_charge_sale_item_id: null })
      .eq("id", day.id);
    if (clearError) throw clearError;
  }
}

export function useSwapStayReservationCoverage() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const { toast } = useToast();
  const { t } = useTranslation("daycareHotel");

  return useMutation({
    mutationFn: swapStayReservationCoverage,
    onSuccess: (_data, variables) => {
      invalidateStayReservationCoverageQueries(queryClient, profile?.current_clinic_id ?? null, variables.reservationId);
    },
    onError: (error, variables) => {
      invalidateStayReservationCoverageQueries(queryClient, profile?.current_clinic_id ?? null, variables.reservationId);
      const errorKey = error instanceof Error && error.message === "daily_rate_not_configured"
        ? "dailyRateNotConfigured"
        : error instanceof Error && error.message === "stay_comanda_not_found"
          ? "comandaNotFound"
          : "error";
      toast({ variant: "destructive", title: t(`planChoice.coverageSwap.${errorKey}`) });
    },
  });
}
